import { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';
import { detectIsMobile, useIsMobile } from '../../hooks/useIsMobile';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message?: string;
  duration?: number;
  onClick?: () => void;
}

type NotifyFn = (notification: Omit<Notification, 'id'>) => void;

let notifyHandler: NotifyFn | null = null;
const notifyQueue: Omit<Notification, 'id'>[] = [];

/** Push a toast from non-React code (e.g. `showAlert`). Queues until the provider mounts. */
export function pushAppNotification(notification: Omit<Notification, 'id'>) {
  if (notifyHandler) {
    notifyHandler(notification);
    return;
  }
  notifyQueue.push(notification);
}

function registerAppNotificationHandler(handler: NotifyFn | null) {
  notifyHandler = handler;
  if (!handler) return;
  const pending = notifyQueue.splice(0);
  for (const item of pending) {
    handler(item);
  }
}

interface NotificationContextType {
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'id'>) => void;
  removeNotification: (id: string) => void;
  clearAllNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const addNotification = useCallback((notification: Omit<Notification, 'id'>) => {
    const id = Math.random().toString(36).substr(2, 9);
    const mobile = detectIsMobile();
    const newNotification: Notification = {
      id,
      // Mobile success toasts confirm something the screen already shows; get out of the way fast.
      duration: mobile && notification.type === 'success' ? 2500 : 5000,
      ...notification,
    };

    // Mobile shows one snackbar at a time: a new one replaces the current one instead of stacking.
    setNotifications(prev => (mobile ? [newNotification] : [...prev, newNotification]));

    // Auto-remove notification after duration
    if (newNotification.duration && newNotification.duration > 0) {
      setTimeout(() => {
        removeNotification(id);
      }, newNotification.duration);
    }
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  useEffect(() => {
    registerAppNotificationHandler(addNotification);
    return () => registerAppNotificationHandler(null);
  }, [addNotification]);

  return (
    <NotificationContext.Provider value={{
      notifications,
      addNotification,
      removeNotification,
      clearAllNotifications,
    }}>
      {children}
      <NotificationContainer />
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}

function NotificationContainer() {
  const { notifications, removeNotification } = useNotifications();
  const isMobile = useIsMobile();

  if (notifications.length === 0) return null;

  if (isMobile) {
    return (
      <div
        className="fixed inset-x-3 z-[9999] mx-auto max-w-md pointer-events-none"
        style={{ bottom: 'calc(max(var(--safe-area-inset-bottom), var(--android-safe-area-bottom, 0px)) + 12px)' }}
      >
        {notifications.map(notification => (
          <MobileNotificationItem
            key={notification.id}
            notification={notification}
            onRemove={() => removeNotification(notification.id)}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="fixed top-4 left-4 right-4 md:left-auto z-[9999] space-y-2 max-w-sm md:w-full">
      {notifications.map(notification => (
        <NotificationItem
          key={notification.id}
          notification={notification}
          onRemove={() => removeNotification(notification.id)}
        />
      ))}
    </div>
  );
}

function notificationIcon(type: NotificationType) {
  switch (type) {
    case 'success':
      return <CheckCircle className="h-5 w-5 text-green-500" />;
    case 'error':
      return <AlertCircle className="h-5 w-5 text-red-500" />;
    case 'warning':
      return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
    case 'info':
      return <Info className="h-5 w-5 text-blue-500" />;
    default:
      return <Info className="h-5 w-5 text-gray-500" />;
  }
}

/** Bottom snackbar: one line, tap or swipe (down/sideways) to dismiss. */
function MobileNotificationItem({
  notification,
  onRemove
}: {
  notification: Notification;
  onRemove: () => void;
}) {
  const [drag, setDrag] = useState({ x: 0, y: 0 });
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  return (
    <div
      role="status"
      className="pointer-events-auto flex items-center gap-3 rounded-lg bg-gray-900 px-4 py-3 text-sm text-white shadow-lg animate-slide-in-up"
      style={drag.x || drag.y ? { transform: `translate(${drag.x}px, ${Math.max(drag.y, 0)}px)` } : undefined}
      onTouchStart={(e) => {
        touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }}
      onTouchMove={(e) => {
        if (!touchStart.current) return;
        setDrag({ x: e.touches[0].clientX - touchStart.current.x, y: e.touches[0].clientY - touchStart.current.y });
      }}
      onTouchEnd={() => {
        touchStart.current = null;
        if (Math.abs(drag.x) > 60 || drag.y > 30) onRemove();
        else setDrag({ x: 0, y: 0 });
      }}
      onClick={(e) => {
        e.stopPropagation();
        notification.onClick?.();
        onRemove();
      }}
    >
      <div className="flex-shrink-0">{notificationIcon(notification.type)}</div>
      <div className="flex-1 min-w-0">
        <p className="font-medium line-clamp-2">{notification.title}</p>
        {/* Success messages mostly restate the title ("The job was created successfully"); errors and alerts carry the actual detail. */}
        {notification.message && notification.type !== 'success' && (
          <p className="mt-0.5 text-gray-300 line-clamp-2">{notification.message}</p>
        )}
      </div>
    </div>
  );
}

function NotificationItem({
  notification,
  onRemove
}: {
  notification: Notification;
  onRemove: () => void;
}) {
  const getIcon = () => notificationIcon(notification.type);

  const getBackgroundColor = () => {
    switch (notification.type) {
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'info':
        return 'bg-blue-50 border-blue-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const getTextColor = () => {
    switch (notification.type) {
      case 'success':
        return 'text-green-800';
      case 'error':
        return 'text-red-800';
      case 'warning':
        return 'text-yellow-800';
      case 'info':
        return 'text-blue-800';
      default:
        return 'text-gray-800';
    }
  };

  return (
    <div
      className={`relative flex items-start p-4 rounded-lg border shadow-lg ${getBackgroundColor()} animate-in slide-in-from-right-full duration-300 ${notification.onClick ? 'cursor-pointer' : ''}`}
      onClick={(e) => {
        if (notification.onClick) {
          e.stopPropagation();
          notification.onClick();
          onRemove();
        }
      }}
    >
      <div className="flex-shrink-0 mr-3">
        {getIcon()}
      </div>

      <div className="flex-1 min-w-0">
        <h4 className={`text-sm font-medium ${getTextColor()}`}>
          {notification.title}
        </h4>
        {notification.message && (
          <p className={`mt-1 text-sm ${getTextColor()} opacompany-90`}>
            {notification.message}
          </p>
        )}
      </div>

      <button
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className={`flex-shrink-0 ml-3 ${getTextColor()} hover:opacompany-70 transition-opacompany`}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

// Convenience functions for common notification types
export const notificationService = {
  success: (title: string, message?: string) => ({
    type: 'success' as const,
    title,
    message,
  }),

  error: (title: string, message?: string) => ({
    type: 'error' as const,
    title,
    message,
  }),

  warning: (title: string, message?: string) => ({
    type: 'warning' as const,
    title,
    message,
  }),

  info: (title: string, message?: string) => ({
    type: 'info' as const,
    title,
    message,
  }),
};
